import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MdReceipt, MdSend, MdCheckCircle, MdPictureAsPdf } from 'react-icons/md';
import { authenticatedFetch, resolveFileUrl } from '../../lib/api';
import { cn } from '../../lib/utils';
import { useToast } from '../shared/Toast';
import { FileDownloadLink } from '../shared/FileDownloadLink';
import { ExtractedInvoicePreview, type ExtractedInvoiceData } from './ExtractedInvoicePreview';

interface AdminInvoicesSectionProps {
    po: any;
    poId: string;
    onExtractedShippingCharges?: (data: {
        charges: Array<{ label: string; amount: string; chargeType: 'shipping' | 'packing' | 'custom' | 'other' }>;
        taxes: Array<{ label: string; rate: string; amount: string }>;
        notes?: string;
    }) => void;
}

export function AdminInvoicesSection({ po, poId, onExtractedShippingCharges }: AdminInvoicesSectionProps) {
    const { data: invoices = [] } = useQuery({
        queryKey: ['admin-procurement-invoices', poId],
        queryFn: async () => {
            const res = await authenticatedFetch(`/api/v1/admin/pos/${poId}/invoices`);
            if (!res.ok) return [];
            const json = await res.json();
            return json.data || [];
        },
        enabled: !!poId,
    });

    const activeProforma = invoices.find((i: any) => i.invoiceType === 'proforma' && i.status !== 'rejected');
    const rejectedProforma = invoices.find((i: any) => i.invoiceType === 'proforma' && i.status === 'rejected');
    const finalInvoice = invoices.find((i: any) => i.invoiceType === 'consolidated' && i.status !== 'rejected');
    const proformaApproved =
        po?.metadata?.proformaInvoiceApproved === true || activeProforma?.status === 'approved';
    const paymentUnlocked = po?.metadata?.paymentUnlocked === true || finalInvoice?.status === 'approved';
    const canSubmit = !['cancelled', 'draft'].includes(String(po.status || '').toLowerCase());
    const canSubmitProforma = canSubmit && (!activeProforma || activeProforma.status === 'rejected');
    const canSubmitFinal = canSubmit && proformaApproved && !finalInvoice;

    return (
        <div className="bg-card rounded-2xl border overflow-hidden shadow-sm">
            <div className="p-6 border-b bg-muted/30">
                <h2 className="text-lg font-black flex items-center gap-2">
                    <MdReceipt className="w-5 h-5 text-primary" />
                    Proforma & Final Invoices
                </h2>
                <p className="text-sm text-muted-foreground mt-1">
                    Send proforma first (item prices & quantities). After the customer approves it, they
                    choose shipping. Then issue the final invoice — it includes shipping charges when
                    applicable and unlocks payment.
                </p>
            </div>

            <div className="p-6 space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
                    <InvoicePanel
                        title="Proforma Invoice"
                        subtitle="Itemized estimate — qty, UOM, prices, charges & taxes"
                        invoiceType="proforma"
                        po={po}
                        poId={poId}
                        existingInvoice={activeProforma}
                        canSubmit={canSubmitProforma}
                        onExtractedShippingCharges={onExtractedShippingCharges}
                    />
                    <InvoicePanel
                        title="Final Invoice"
                        subtitle={
                            proformaApproved
                                ? 'Issue after shipping is settled (or self shipment confirmed)'
                                : 'Disabled until customer approves the proforma'
                        }
                        invoiceType="consolidated"
                        po={po}
                        poId={poId}
                        existingInvoice={finalInvoice}
                        canSubmit={canSubmitFinal}
                    />
                </div>

                {rejectedProforma && !activeProforma && (
                    <div className="p-4 bg-red-50 dark:bg-red-950/30 border border-red-100 dark:border-red-800/40 rounded-lg text-sm text-red-800 dark:text-red-300">
                        <p className="font-medium">Proforma rejected — revise and resubmit</p>
                        {rejectedProforma.rejectionReason && (
                            <p className="mt-1">Reason: {rejectedProforma.rejectionReason}</p>
                        )}
                    </div>
                )}

                {paymentUnlocked && (
                    <div className="flex items-center gap-2 p-4 bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-100 dark:border-emerald-800/40 rounded-lg text-sm text-emerald-800 dark:text-emerald-300 font-medium">
                        <MdCheckCircle className="w-5 h-5 shrink-0" />
                        Proforma accepted — final invoice issued and payment unlocked.
                    </div>
                )}

                {activeProforma?.status === 'submitted' && !finalInvoice && (
                    <div className="flex items-center gap-2 p-4 bg-amber-50 dark:bg-amber-950/30 border border-amber-100 dark:border-amber-800/40 rounded-lg text-sm text-amber-900 dark:text-amber-200 font-medium">
                        <MdCheckCircle className="w-5 h-5 shrink-0" />
                        Proforma sent. Waiting for customer approval.
                    </div>
                )}
            </div>
        </div>
    );
}

function InvoicePanel({
    title,
    invoiceType,
    po,
    poId,
    existingInvoice,
    canSubmit,
    subtitle,
    onExtractedShippingCharges,
}: {
    title: string;
    subtitle: string;
    invoiceType: 'proforma' | 'consolidated';
    po: any;
    poId: string;
    existingInvoice?: any;
    canSubmit: boolean;
    onExtractedShippingCharges?: AdminInvoicesSectionProps['onExtractedShippingCharges'];
}) {
    const { showToast } = useToast();
    const queryClient = useQueryClient();
    const [notes, setNotes] = useState('');
    const [uploadFiles, setUploadFiles] = useState<File[]>([]);
    const [extracted, setExtracted] = useState<ExtractedInvoiceData | null>(null);
    const [parsing, setParsing] = useState(false);

    const parsePdf = async (file: File) => {
        setParsing(true);
        setExtracted(null);
        try {
            const formData = new FormData();
            formData.append('document', file);
            formData.append('invoiceType', invoiceType);
            const res = await authenticatedFetch(`/api/v1/admin/pos/${poId}/invoices/parse-pdf`, {
                method: 'POST',
                body: formData,
            });
            const json = await res.json();
            if (!res.ok) throw new Error(json.error || 'Failed to read PDF');
            setExtracted(json.data.extracted);
            if (invoiceType === 'proforma' && onExtractedShippingCharges && json.data.extracted) {
                const ext = json.data.extracted;
                const charges = (ext.vendorCharges || []).map((c: any) => ({
                    label: c.label || '',
                    amount: String(c.amount ?? ''),
                    chargeType: (c.chargeType || 'custom') as 'shipping' | 'packing' | 'custom' | 'other',
                }));
                const taxRows = (ext.taxes || []).map((t: any) => ({
                    label: t.label || '',
                    rate: t.rate !== undefined ? String(t.rate) : '',
                    amount: String(t.amount ?? ''),
                }));
                if (charges.length || taxRows.length) {
                    onExtractedShippingCharges({ charges, taxes: taxRows, notes: ext.notes });
                }
            }
            showToast('Invoice data read from PDF. Review below before sending.', 'success');
        } catch (err: any) {
            showToast(err.message || 'PDF parse failed', 'error');
        } finally {
            setParsing(false);
        }
    };

    const submitMutation = useMutation({
        mutationFn: async (files: File[]) => {
            const formData = new FormData();
            formData.append('invoiceType', invoiceType);
            if (notes.trim()) formData.append('notes', notes.trim());
            if (extracted) formData.append('extractedData', JSON.stringify(extracted));
            files.forEach((f) => formData.append('documents', f));

            const res = await authenticatedFetch(`/api/v1/admin/pos/${poId}/invoices`, {
                method: 'POST',
                body: formData,
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.error || 'Failed to submit invoice');
            }
            return res.json();
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin-procurement-invoices', poId] });
            queryClient.invalidateQueries({ queryKey: ['admin-po-details', poId] });
            queryClient.invalidateQueries({ queryKey: ['admin-offers'] });
            queryClient.invalidateQueries({ queryKey: ['admin-offer-detail'] });
            setUploadFiles([]);
            setExtracted(null);
            setNotes('');
            showToast(`${title} submitted successfully.`, 'success');
        },
        onError: (err: Error) => showToast(err.message, 'error'),
    });

    return (
        <div className="flex h-full min-h-[280px] flex-col rounded-xl border border-[#0088cc]/30 bg-[#e8f4fb] p-5 dark:border-[#0088cc]/35 dark:bg-[#0f2744]">
            <div className="min-h-[52px]">
                <p className="text-[10px] font-bold uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
                    {title}
                </p>
                <p className="mt-0.5 text-xs text-[hsl(var(--muted-foreground))]">{subtitle}</p>
            </div>

            {existingInvoice ? (
                <div className="mt-4 flex flex-1 flex-col gap-3">
                    <p className="font-bold text-[hsl(var(--foreground))]">{existingInvoice.invoiceNumber}</p>
                    <p
                        className={cn(
                            'inline-flex w-fit items-center rounded-full px-2.5 py-1 text-[10px] font-black uppercase tracking-wider',
                            existingInvoice.status === 'approved'
                                ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400'
                                : existingInvoice.status === 'rejected'
                                  ? 'bg-red-500/15 text-red-600 dark:text-red-400'
                                  : 'bg-[#0088cc]/15 text-[#0088cc]'
                        )}
                    >
                        {existingInvoice.status}
                    </p>
                    <p className="text-sm font-semibold text-[hsl(var(--foreground))]">
                        {po.currency} {Number(existingInvoice.grandTotal).toFixed(2)}
                    </p>

                    <div className="mt-auto space-y-2 pt-2">
                        {existingInvoice ? (
                            <FileDownloadLink
                                href={
                                    existingInvoice.documents?.[0]?.fileUrl &&
                                    (existingInvoice.documents[0].fileUrl.startsWith('http://') ||
                                     existingInvoice.documents[0].fileUrl.startsWith('https://') ||
                                     existingInvoice.documents[0].fileUrl.startsWith('uploads/') ||
                                     existingInvoice.documents[0].fileUrl.startsWith('/uploads/'))
                                        ? resolveFileUrl(existingInvoice.documents[0].fileUrl)
                                        : `/api/v1/admin/procurement-invoices/${existingInvoice._id}/pdf`
                                }
                                fileName={existingInvoice.documents?.[0]?.fileName || `${existingInvoice.invoiceNumber}.pdf`}
                                className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#0088cc] px-4 py-2.5 text-xs font-bold text-white shadow-sm transition hover:bg-[#0077b3]"
                                onError={(message) => showToast(message, 'error')}
                            >
                                <MdPictureAsPdf className="h-4 w-4" />
                                View uploaded PDF
                            </FileDownloadLink>
                        ) : (
                            <span className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-[#0088cc]/25 px-4 py-2.5 text-xs font-bold text-[hsl(var(--muted-foreground))]">
                                No PDF uploaded
                            </span>
                        )}
                    </div>
                </div>
            ) : canSubmit ? (
                <div className="mt-4 flex flex-1 flex-col gap-4">
                    <div>
                        <label className="text-[10px] font-bold uppercase text-[hsl(var(--muted-foreground))]">
                            Upload invoice (PDF)
                        </label>
                         <input
                             type="file"
                             accept=".pdf,image/*"
                             onChange={(e) => {
                                 const list = e.target.files ? Array.from(e.target.files) : [];
                                 setUploadFiles(list);
                                 const pdf = list.find((f) => f.name.toLowerCase().endsWith('.pdf'));
                                 if (pdf) parsePdf(pdf);
                             }}
                             className="mt-2 w-full text-xs text-zinc-500 file:mr-2 file:bg-transparent file:border-0 file:p-0 file:text-[#0088cc] file:font-bold file:underline hover:file:text-[#0077b3] file:cursor-pointer cursor-pointer"
                         />
                        {parsing && (
                            <p className="mt-2 animate-pulse text-xs font-medium text-[#0088cc]">
                                Reading PDF…
                            </p>
                        )}
                        {uploadFiles.length > 0 && (
                            <ul className="mt-2 space-y-1">
                                {uploadFiles.map((f, i) => (
                                    <li key={i} className="text-xs font-medium text-[hsl(var(--muted-foreground))]">
                                        {f.name}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    {extracted && (
                        <ExtractedInvoicePreview
                            data={extracted}
                            currency={po.currency || 'USD'}
                            chargesOnly={invoiceType === 'consolidated'}
                        />
                    )}

                    <div>
                        <label className="text-[10px] font-bold uppercase text-[hsl(var(--muted-foreground))]">
                            Notes (optional)
                        </label>
                        <textarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            rows={2}
                            className="mt-1 w-full resize-none rounded-lg border border-[#0088cc]/20 bg-[hsl(var(--background))] px-3 py-2 text-sm"
                            placeholder="Notes for the customer…"
                        />
                    </div>

                    <button
                        type="button"
                        onClick={() => {
                            if (uploadFiles.length === 0) {
                                showToast('Please upload an invoice PDF first.', 'error');
                                return;
                            }
                            submitMutation.mutate(uploadFiles);
                        }}
                        disabled={submitMutation.isPending}
                        className="mt-auto inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#0088cc] px-4 py-2.5 text-xs font-bold text-white transition hover:bg-[#0077b3] disabled:opacity-50"
                    >
                        <MdSend className="h-4 w-4" />
                        {submitMutation.isPending ? 'Sending…' : `Send ${title} to Customer`}
                    </button>
                </div>
            ) : (
                <p className="mt-4 text-sm text-[hsl(var(--muted-foreground))]">Not available yet</p>
            )}
        </div>
    );
}
